import ITimeKeeper from 'Models/ITimeKeeper';
import TimeKeeper from 'Models/TimeKeeper';
import { createTimeKeeper } from 'Models/createTimeKeeper';
import { ReactToPhaserListeners, NotifyReactEvents } from 'Phaser/EventBus2';
import CursorMobile, {
  EventStreamPhaser2,
  IteratorExtensible,
} from 'Phaser/GameObjects/CursorMobile';
import IPlayable from 'Phaser/GameObjects/Playable';
import {
  MediumConfig,
  MinimumConfig,
  PhraseData,
  PhraseGraphic,
  ScaledConfig,
} from 'Phaser/GameObjects/StaffLine';
import ExerciseBase, { IConfig, IObjective } from './ExerciseBase';
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import { TimingOffsetsConfig } from 'Models/EventStream';
import TimeSignature from 'Models/TimeSignature';
import Phrase, { Iterator } from 'Models/Phrase';
import { awsRum } from 'Utils/AwsRum';
import { ProgressState } from 'Phaser/GameObjects/Progress';
import MusicXML from 'Models/MusicXML';
import logger from 'Utils/Logger';
import { reportError } from 'Utils/reportError';
import { SuspendGuard } from 'Utils/suspendGuard';
import { CursorData } from 'Phaser/GameObjects/StaffLine/Types';
import { optimizeStaffForOptions } from 'Phaser/GameObjects/StaffLine/Util';
import { COLORBLIND_TYPE, isCapacitorNative } from 'Utils/Constants';
import { notifyAppAudioOutput } from 'AudioRecognition/appAudioSuppression';
import MuseFlowAudio from 'Utils/MuseFlowAudioPlugin';
import { metronomeSoundConfig } from 'Volume/volumeOutput';
import { tutorialCountInBeats } from 'Utils/playAlong/tutorialPlayAlong';

export class MusicXMLDummy extends MusicXML {
  static getElements() {
    return [];
  }
}

export interface INoteGraphicsDatabase {
  getNoteById: (id: string, phraseTimestamp?: number) => IPlayable | null;
  getPhraseEndPosition: () => number;
  getNotePosition: (id: string, timestamp?: number, rest?: boolean) => number | null;
}

export type PhraseObjectiveData = {
  cursorData: CursorData;
  uuid: string;
  phrase: Phrase;
  timeSignature: TimeSignature;
};

interface Objective extends IObjective {
  tempo?: number;
  paddingHorizontal?: number;
  phraseDetails?: { svgUrl: string; jsonUrl: string; id: string }[];
  phrases?: PhraseObjectiveData[];
}

export interface Config extends IConfig<Objective> {
  transition?: 'Wipe' | 'Dissolve';
  cbType?: COLORBLIND_TYPE | null;
}

export enum PlayingState {
  WaitingForInput,
  Playing,
  Stopped,
}

export default class StaffExercise
  extends ExerciseBase<Objective, Config>
  implements INoteGraphicsDatabase
{
  static METRONOME_SCHEDULE_AHEAD_TIME = 0;

  staffLines: PhraseGraphic[] = [];
  cursor?: CursorMobile;
  timers: Phaser.Time.TimerEvent[] = [];

  eventStream?: EventStreamPhaser2;
  timeKeeper: ITimeKeeper;
  lastMeasure?: number;

  clickAudio?: Phaser.Sound.BaseSound;
  clickAudioDownbeat?:
    | Phaser.Sound.WebAudioSound
    | Phaser.Sound.HTML5AudioSound
    | Phaser.Sound.NoAudioSound;
  lastTickTime?: number;

  playingState: PlayingState = PlayingState.Stopped;

  beatNumber?: number;
  timeSig?: TimeSignature;

  private nativeMetronomeStarted = false;

  // Ungraded: metronome clicks heard so far in the count-in that starts the
  // run on its own (null when no such count-in is running).
  private countInClicksHeard: number | null = null;
  // Ungraded: the count-in about to start follows the pass before it straight
  // away (Keep practicing), so the click on the beat that ended that pass is
  // its first. A pass ends inside a frame's update; one that follows straight
  // away starts its count-in in that same frame.
  private countInContinuesLoop = false;
  private framesUpdated = 0;
  private runEndedOnFrame = -1;

  // MUS-175: detects the suspend/resume wall-clock jump (laptop sleep, lid
  // close) so a single resumed frame cannot falsely complete the exercise.
  private readonly suspendGuard = new SuspendGuard();

  constructor() {
    super('StaffExercise');
    this.timeKeeper = createTimeKeeper();
  }

  create() {
    NotifyReactEvents.CurrentSceneReady(this);
  }

  // ==================================================================
  // Scene init
  // ==================================================================

  init(config: Config) {
    this.sound.pauseOnBlur = false;
    this.playingState = PlayingState.Stopped;
    this.nativeMetronomeStarted = false;
    NotifyReactEvents.CurrentSceneReady(this);
    super.init(config);
    if (!isCapacitorNative() && !this.clickAudio) {
      if (this.cache.audio.exists('click')) {
        this.clickAudio = this.sound.add('click');
      } else {
        reportError(new Error('[StaffExercise] "click" audio not in cache'), 'audio.click');
      }
    }
    if (!isCapacitorNative() && !this.clickAudioDownbeat) {
      if (this.cache.audio.exists('click-down')) {
        this.clickAudioDownbeat = this.sound.add('click-down');
      } else {
        reportError(new Error('[StaffExercise] "click-down" audio not in cache'), 'audio.click');
      }
    }
    this.boundCallbacks.set('SetNoteState', this.setNoteState as (...args: never[]) => void);
    ReactToPhaserListeners.SetNoteState(this.setNoteState, this);
    this.boundCallbacks.set('PulseNote', this.pulseNote as (...args: never[]) => void);
    ReactToPhaserListeners.PulseNote(this.pulseNote, this);
  }

  // ==================================================================
  // IExerciseSceneFacade overrides
  // ==================================================================

  loadObjective(objectiveIndex: number): void {
    this.currentObjective = objectiveIndex;
    const objective = this.config?.objectives[objectiveIndex];
    if (objective) {
      this.showInstruction(objective.text ?? 'Play this line of music in sync with the metronome.');
    }
    this.buildStaffLines();
    this.dispatchExerciseEvent({ type: 'OBJECTIVE_LOADED' });
  }

  initExercise(_objectiveIndex: number): void {
    this.lastMeasure = undefined;
    this.lastTickTime = undefined;
    this.playingState = PlayingState.WaitingForInput;
    const firstPhraseData = this.config!.objectives[this.currentObjective ?? 0].phrases![0]!;
    const { phrase, timeSignature: timeSig } = firstPhraseData;
    const cursorData = firstPhraseData.cursorData.serializedIterator;
    this.timeKeeper.setTimeSignatures(0, [timeSig]);
    this.timeKeeper.resetTime();
    this.eventStream = new EventStreamPhaser2(
      this.timeKeeper!,
      new TimingOffsetsConfig(),
      this.config?.cbType ?? null,
    );
    const phraseGraphic = this.staffLines[0];
    if (!phraseGraphic) {
      console.warn('[StaffExercise] initExercise: no staff lines loaded');
      return;
    }
    const phraseHeight = phraseGraphic.getInnerHeight();
    const cursorHeight = phraseHeight * 1.25;
    const cursorY = phraseGraphic.y + (phraseHeight - cursorHeight) / 2;
    this.cursor = new CursorMobile(
      this,
      phraseGraphic.getFirstNotePosition(),
      cursorY,
      40 * 1,
      cursorHeight,
      this.eventStream,
      new IteratorExtensible(this, [], phrase, timeSig),
      cursorData,
      undefined,
      this.onFinishPlaying,
      this,
    );
    this.cursor.reset(new IteratorExtensible(this, [], phrase, timeSig), 0, 0);
    this.cursor.addPhrase(cursorData, phraseGraphic._data.uuid, timeSig, 0);
    this.eventStream?.addEvents2(new Iterator(cursorData, phrase), phrase.getStartTimestamp());
    this.cursor.enable();
    if (this.graded) {
      this.resumePlayback();
      return;
    }
    // Ungraded: nothing waits for a first note. The cursor waits on the first
    // note while a 1-bar count-in clicks, then the line plays on its own.
    this.playingState = PlayingState.Stopped;
    this.startPlayAlongPass((continuesLoop) => {
      this.playingState = PlayingState.WaitingForInput;
      this.countInContinuesLoop = continuesLoop && this.runEndedOnFrame === this.framesUpdated;
      this.resumePlayback();
    });
  }

  protected loopsWhilePracticing(): boolean {
    return true;
  }

  evaluatePerformance(): void {
    if (!this.passWasHeard()) {
      this.dispatchExerciseEvent({ type: 'RUN_ENDED' });
      return;
    }
    const success = this.staffLines.reduce((prev, curr) => prev && curr.checkAllNotes(), true);
    if (success) {
      this.dispatchExerciseEvent({ type: 'OBJECTIVE_PASSED' });
    } else {
      this.dispatchExerciseEvent({ type: 'OBJECTIVE_FAILED' });
    }
  }

  stopPlayback(): void {
    this.cursor?.destroy();
    this.playingState = PlayingState.Stopped;
    this.stopNativeMetronome();
  }

  showPassAnimation(onComplete: () => void): void {
    // Fade out staff lines during the pass animation
    this.tweens.add({
      targets: [this.staffLines],
      alpha: 0,
      duration: 500,
    });
    super.showPassAnimation(onComplete);
  }

  showFailAnimation(_failedAttempts: number): void {
    this.staffLines.forEach((line) => line.notes.forEach((note) => note.reset(false)));
    this.eventStream?.reset();
    this.failExercise('Good effort! Try again.');
  }

  /** The staff as drawn: stems, ledger lines and low notes included. */
  protected getNotationBottom(): number | undefined {
    if (this.staffLines.length === 0) return undefined;
    return Math.max(...this.staffLines.map((staff) => staff.getBounds().bottom));
  }

  pauseScene(): void {
    this.cursor?.pause();
    this.lastTickTime = undefined;
    this.timeKeeper.pause();
    this.stopNativeMetronome();
    this.paused = true;
    this.scene.launch('PauseMenu');
    NotifyReactEvents.ExercisePaused({});
  }

  resumeScene(): void {
    this.paused = false;
    // Ungraded: with no run under way (its count-in not started yet, or the
    // Next ring running) there is nothing to play on.
    if (this.graded || this.playingState !== PlayingState.Stopped) this.resumePlayback();
    NotifyReactEvents.ExerciseResumed();
  }

  unloadGraphics() {
    this.timers.forEach((timer) => {
      if (timer && timer.destroy) {
        timer.destroy();
      }
    });
    this.timers = [];
    this.staffLines.forEach((staff) => staff?.destroy());
    this.staffLines = [];
    if (this.cursor) {
      this.cursor.destroy();
      this.cursor = undefined;
    }
    if (this.eventStream) {
      this.eventStream.reset();
      this.eventStream = undefined;
    }
    if (this.timeKeeper) {
      this.timeKeeper.pause();
      this.timeKeeper.resetTime();
    }
    this.stopNativeMetronome();
    this.playingState = PlayingState.Stopped;
    this.lastTickTime = undefined;
    this.lastMeasure = undefined;
  }

  // ==================================================================
  // Native metronome
  // ==================================================================

  private startNativeMetronome(bpm: number) {
    console.log(
      `[EXERCISE-DBG] StaffExercise.startNativeMetronome bpm=${bpm} alreadyStarted=${this.nativeMetronomeStarted}`,
    );
    if (!isCapacitorNative() || this.nativeMetronomeStarted) return;
    this.nativeMetronomeStarted = true;
    const timeSig = this.timeSig ?? new TimeSignature(4, 4);
    MuseFlowAudio.setMetronomeEnabled({ enabled: true });
    // MUS-282: during the flexible count-in (cursor blinking, waiting for the
    // user) the metronome should play uniform clicks with no accented downbeat.
    // Accents are enabled when the user starts playing (see the transition to
    // PlayingState.Playing). Matches the lesson/repertoire count-in.
    MuseFlowAudio.setDownbeatsEnabled({ enabled: false });
    MuseFlowAudio.startPlayback({
      bpm,
      timeSigNumerator: timeSig.numerator,
      timeSigDenominator: timeSig.denominator,
      countInMeasures: 0,
      startMeasure: 0,
      tickNoteType: NoteType._32nd,
    });
  }

  private stopNativeMetronome() {
    if (!isCapacitorNative() || !this.nativeMetronomeStarted) return;
    this.nativeMetronomeStarted = false;
    MuseFlowAudio.pausePlayback();
  }

  // ==================================================================
  // Metronome click (web audio fallback for non-native)
  // ==================================================================

  private metronomeClick() {
    if (this.timeSig === undefined || this.playingState === PlayingState.Stopped) return;

    if (this.beatNumber === this.timeSig.numerator - 1) {
      this.beatNumber = 0;
    } else if (this.beatNumber !== undefined) {
      this.beatNumber++;
    }

    if (isCapacitorNative()) return;

    try {
      this.clickAudio!.stop();
      this.clickAudioDownbeat!.stop();
    } catch (error) {
      console.warn('Failed to stop click audio:', error);
      awsRum?.recordError(error as Error);
    }
    if (this.beatNumber === 0 && this.playingState === PlayingState.Playing) {
      try {
        notifyAppAudioOutput({ reason: 'metronome' });
        this.clickAudioDownbeat?.play(metronomeSoundConfig());
      } catch (error) {
        console.warn('Failed to play downbeat audio:', error);
        awsRum?.recordError(error as Error);
      }
    } else {
      try {
        notifyAppAudioOutput({ reason: 'metronome' });
        this.clickAudio!.play(metronomeSoundConfig());
      } catch (error) {
        console.warn('Failed to play click audio:', error);
        awsRum?.recordError(error as Error);
      }
    }
  }

  // ==================================================================
  // Playback control
  // ==================================================================

  private resumePlayback() {
    if (
      !this.config ||
      this.currentObjective === undefined ||
      this.currentObjective < 0 ||
      this.currentObjective >= this.config.objectives.length
    ) {
      return;
    }

    const objective = this.config.objectives[this.currentObjective];
    if (!objective) return;

    // MUS-175: playback is (re)starting with a freshly re-anchored clock, so
    // drop any suspend baseline - the first sampled frame after this must only
    // re-establish it, never read the away-gap as elapsed play time.
    this.suspendGuard.reset();

    const { tempo, phrases } = objective;
    const beatDuration = (60 / (tempo ?? 72)) * 1000;
    this.beatNumber = -1;
    this.startNativeMetronome(tempo ?? 72);
    // Ungraded, waiting on the first note: this click starts the count-in.
    this.countInClicksHeard =
      !this.graded && this.playingState === PlayingState.WaitingForInput ? 0 : null;
    this.metronomeClick();
    // On native this click is silent (the native metronome plays the clicks,
    // and its first is the next beat), so only a click that sounds counts.
    // Except when this count-in follows the pass before it straight away: the
    // native metronome of that pass had already queued the click on the beat
    // that ended it, and that click sounds. It is the count-in's first, as the
    // click above is on the web.
    const firstClickSounds = !isCapacitorNative() || this.countInContinuesLoop;
    this.countInContinuesLoop = false;
    if (firstClickSounds) this.tallyCountInClick();

    if (phrases && phrases.length > 0) {
      this.timeKeeper.resetTime();
      const secondsPerMeasure = (60 / (tempo ?? 72)) * phrases[0].timeSignature.numerator;
      const measureOffset = TimeKeeper.METRONOME_SCHEDULE_AHEAD_TIME / secondsPerMeasure;
      this.timeKeeper?.unpause(
        tempo ?? 72,
        1,
        (this.lastMeasure !== undefined ? this.lastMeasure + 1 : 0) + measureOffset,
        NoteType._32nd,
      );
    }

    if (this.staffLines && this.staffLines.length > 0) {
      const startPos = this.staffLines[0].getFirstNotePosition(this.lastMeasure ?? 0);
      this.cursor?.setStart(
        startPos,
        (this.lastMeasure ?? 0) * this.timeSig!.numerator * 0.25 - 0.25,
        this.lastMeasure,
      );
      this.cursor?.unpause();
      this.cursor?.blink(beatDuration);
    }
  }

  // ==================================================================
  // Update loop
  // ==================================================================

  update() {
    this.framesUpdated += 1;
    if (this.paused || this.currentObjective === undefined) return;
    if (
      !this.config ||
      this.currentObjective < 0 ||
      this.currentObjective >= this.config.objectives.length
    ) {
      return;
    }
    const objective = this.config.objectives[this.currentObjective];
    if (!objective) return;

    if (this.playingState !== PlayingState.Stopped) {
      // MUS-175: a device suspend/resume (laptop sleep, lid close, OS suspend)
      // produces a single frame whose wall-clock delta is the entire away
      // duration. The audio clock is free-running, so left unguarded that jump
      // pushes audioTimeToMeasureTimestamp() past the phrase total duration and
      // the exercise falsely completes with no user input. Sample every active
      // frame (keeping the baseline fresh across WaitingForInput -> Playing);
      // when the jump lands mid-play, treat it as a disruption: pause and wait
      // for a manual resume. Resume re-anchors the clock (resumePlayback ->
      // timeKeeper.unpause), so the stale delta is discarded there.
      const suspended = this.suspendGuard.sample();
      if (suspended && this.playingState === PlayingState.Playing) {
        this.dispatchExerciseEvent({ type: 'PAUSE_EXERCISE' });
        return;
      }

      const timestamp = this.timeKeeper.audioTimeToMeasureTimestamp();
      const { tempo } = objective;
      if (this.lastTickTime === undefined) {
        this.lastTickTime = timestamp - StaffExercise.METRONOME_SCHEDULE_AHEAD_TIME;
      }

      const beatFraction = 1 / 4;
      const measureDurationSinceLastTickTime = timestamp - this.lastTickTime;
      if (this.playingState === PlayingState.Playing) {
        // Ungraded: nothing is heard, so no note is marked missed (no colours).
        if (this.graded) this.eventStream?.update();
        this.lastMeasure = Math.max(this.lastMeasure ?? 0, Math.floor(timestamp) - 1);
        this.cursor?.clearBlink();
        this.cursor?.update();
        // The run ended in that update (and, ungraded with Keep practicing,
        // the next pass may already have started its count-in, click and
        // all): the beat measured above belongs to the run that ended.
        if (this.playingState !== PlayingState.Playing) return;
      }
      if (measureDurationSinceLastTickTime > beatFraction) {
        this.metronomeClick();
        if (this.playingState === PlayingState.WaitingForInput) {
          const beatDuration = (60 / (tempo ?? 72)) * 1000;
          this.cursor?.blink(beatDuration);
        }
        this.lastTickTime =
          this.lastTickTime === undefined
            ? undefined
            : timestamp - (timestamp - this.lastTickTime - 0.25);
        if (this.tallyCountInClick() && this.lastTickTime !== undefined) {
          this.startPlayAlongRun(timestamp - this.lastTickTime);
        }
      }
    }
  }

  /**
   * Ungraded: count a count-in click. True on the click after a full bar of
   * them: the first note's beat, where the run starts.
   */
  private tallyCountInClick(): boolean {
    if (this.countInClicksHeard === null || this.playingState !== PlayingState.WaitingForInput) {
      return false;
    }
    this.countInClicksHeard += 1;
    const timeSig = this.timeSig ?? new TimeSignature(4, 4);
    const beats = tutorialCountInBeats(timeSig.numerator / timeSig.denominator, 1 / 4);
    return this.countInClicksHeard > beats;
  }

  /**
   * Ungraded: the count-in reached the first note's beat. Start the run as a
   * key played right on that beat starts it, with no note played.
   * `sinceBeat` is how far past the beat this frame is (whole notes).
   */
  private startPlayAlongRun(sinceBeat: number) {
    if (
      !this.config ||
      this.currentObjective === undefined ||
      this.currentObjective >= this.config.objectives.length
    ) {
      return;
    }
    const { tempo, phrases } = this.config.objectives[this.currentObjective];
    if (!phrases) return;
    this.countInClicksHeard = null;
    this.beginTimedRun(tempo ?? 72, phrases[0].timeSignature, Math.max(0, sinceBeat));
    this.dispatchExerciseEvent({ type: 'AUTO_START' });
  }

  // ==================================================================
  // MIDI input — routes through state machine
  // ==================================================================

  midiOn({ note, velocity }: { note: number; velocity: number }): void {
    if (this.paused || !this.hearsNotes()) return;
    const phase = this.getExercisePhase();

    if (phase === 'waitingForInput') {
      if (
        !this.config ||
        this.currentObjective === undefined ||
        this.currentObjective >= this.config.objectives.length
      ) {
        return;
      }
      const objective = this.config.objectives[this.currentObjective];
      const { tempo, phrases } = objective;
      if (!phrases) return;

      this.staffLines.forEach((line) => line.notes.forEach((n) => n.reset(true)));
      const timestamp = this.timeKeeper.audioTimeToMeasureTimestamp();
      if (!this.lastTickTime) return;
      const timeDiff = timestamp - this.lastTickTime;
      if (timeDiff > 0.06 && timeDiff < 0.125) {
        this.showToast('Too Late');
      } else if (timeDiff >= 0.125 && timeDiff < 0.19) {
        this.showToast('Too early');
      } else {
        const inaccCompensation = timeDiff > 0.125 ? timeDiff - 0.25 : timeDiff;
        this.startPlaying(tempo ?? 72, note, velocity, phrases[0].timeSignature, inaccCompensation);
        this.dispatchExerciseEvent({ type: 'NOTE_PLAYED', note, velocity });
      }
    } else if (phase === 'playing') {
      this.eventStream?.handleNoteOnEvent(note, velocity);
    }
  }

  midiOff({ note }: { note: number }): void {
    if (!this.paused && this.hearsNotes()) this.eventStream?.handleNoteOffEvent(note);
  }

  private startPlaying(
    bpm: number,
    note: number,
    velocity: number,
    timeSig: TimeSignature,
    inaccCompensation: number,
  ) {
    this.beginTimedRun(bpm, timeSig, inaccCompensation);
    this.eventStream?.handleNoteOnEvent(note, velocity);
  }

  /** Start the run on the beat the first note falls on (no note is played here). */
  private beginTimedRun(bpm: number, timeSig: TimeSignature, inaccCompensation: number) {
    if (inaccCompensation < 0) {
      this.beatNumber = -1;
    } else {
      this.beatNumber = 0;
    }
    this.lastTickTime = inaccCompensation < 0 ? -0.25 : 0;
    this.playingState = PlayingState.Playing;
    // MUS-175: entering Playing on the first note - re-baseline the suspend
    // guard so this transition is never misread as a suspend gap.
    this.suspendGuard.reset();
    // MUS-282 follow-up: the user is now playing, so the accented downbeat must
    // align to *their* first note (the musical beat 0), not to wherever the
    // count-in happened to begin. The native scheduler anchors downbeats to its
    // own startTime, which was pinned to the count-in start, so simply enabling
    // accents would put them on the wrong beat. Re-anchor by restarting native
    // playback at this note — startMeasure mirrors the timeKeeper.unpause below
    // so the click track and the music share the same beat 0 — then enable the
    // accents that were suppressed during the flexible count-in.
    if (isCapacitorNative()) {
      MuseFlowAudio.setDownbeatsEnabled({ enabled: true });
      MuseFlowAudio.startPlayback({
        bpm,
        timeSigNumerator: timeSig.numerator,
        timeSigDenominator: timeSig.denominator,
        countInMeasures: 0,
        startMeasure: inaccCompensation,
        tickNoteType: NoteType._32nd,
      });
    }
    this.cursor?.unpause(inaccCompensation);
    this.timeKeeper.unpause(bpm, 0, inaccCompensation, NoteType._32nd);
  }

  // ==================================================================
  // Cursor finish callback — dispatches PHRASE_END to machine
  // ==================================================================

  onFinishPlaying() {
    if (this.paused) return;
    this.runEndedOnFrame = this.framesUpdated;
    this.dispatchExerciseEvent({ type: 'PHRASE_END' });
  }

  // ==================================================================
  // Staff line building
  // ==================================================================

  private buildStaffLines() {
    this.staffLines.forEach((staff) => staff?.destroy());
    this.staffLines = [];
    const x = 0;
    this.staffLines = this.config!.objectives[this.currentObjective ?? 0].phrases!.map(
      ({ cursorData, uuid, phrase, timeSignature }, idx) => {
        const data = new PhraseData(
          phrase,
          idx,
          cursorData,
          uuid,
          timeSignature,
          true,
          true,
          true,
          true,
          0,
        );
        this.timeSig = timeSignature;
        return new PhraseGraphic(
          this,
          x,
          {
            globalScaling: 1,
            pitchLineGap: 20,
            pitchLineWidth: 2,
            measureLineWidth: 3,
            clefGap: 100,
            centreAlign: false,
            doubleEndLines: true,
          },
          data,
          'None',
          0,
          0,
        );
      },
    );
    const width = this.cameras.main.width / 2;
    const height = 225;
    const actualVpHeight = this.cameras.main.height / 2;
    const props = {
      minPaddingX: 75,
      minPaddingY: 0,
      idealPhraseConfig: MediumConfig(),
      minPhraseConfig: MinimumConfig(),
      minStaffSpacingVertical: 35,
      idealPaddingX: 75,
      idealPaddingY: 0,
    };
    const extraStaffParams = {
      topClefNumExtraLinesTop: 4,
      topClefNumExtraLinesBottom: 2,
      bottomClefNumExtraLinesTop: 2,
      bottomClefNumExtraLinesBottom: 4,
    };
    const minAspectRatio = 4.5;
    const maxAspectRatio = 8;
    const resizeParams = optimizeStaffForOptions(
      [width, height],
      props,
      extraStaffParams,
      minAspectRatio,
      maxAspectRatio,
      1,
      false,
    );
    const dpr = 1;
    if (resizeParams) {
      const maxWidth = resizeParams.phraseDimens.innerHeight * maxAspectRatio;
      const availableWidth = width - resizeParams.paddingX * 2;
      const phraseWidth = Math.min(availableWidth, maxWidth) / dpr;
      const phraseHeight = resizeParams.phraseDimens.innerHeight / dpr;
      const scaledPhraseParams = {
        paddingX: resizeParams.paddingX / dpr,
        paddingY: resizeParams.paddingY / dpr,
        staffSpacingVertical: resizeParams.staffSpacingVertical / dpr,
        phraseRenderingConfig: {
          ...ScaledConfig(resizeParams.phraseRenderingConfig, 1 / dpr),
          doubleEndLines: true,
        },
        phraseDimens: {
          ...resizeParams.phraseDimens,
          bbHeight: resizeParams.phraseDimens.bbHeight / dpr,
          innerHeight: resizeParams.phraseDimens.innerHeight / dpr,
          topClefHeight: {
            outerHeight: resizeParams.phraseDimens.topClefHeight.outerHeight / dpr,
            innerHeight: resizeParams.phraseDimens.topClefHeight.innerHeight / dpr,
          },
          bottomClefHeight: {
            outerHeight: resizeParams.phraseDimens.bottomClefHeight.outerHeight / dpr,
            innerHeight: resizeParams.phraseDimens.bottomClefHeight.innerHeight / dpr,
          },
        },
      };
      const phraseY = (actualVpHeight / dpr - phraseHeight) / 2 + scaledPhraseParams.paddingY;
      this.staffLines.forEach((staff) => {
        staff.renderDpr = 2;
        staff.resize(
          scaledPhraseParams.phraseRenderingConfig,
          phraseWidth,
          scaledPhraseParams.paddingX,
          phraseY,
          scaledPhraseParams.phraseDimens,
          scaledPhraseParams.phraseDimens.innerHeight,
        );
        staff.transition(this.currentObjective === 0 ? 'Draw' : 'Fade');
      });
    }
  }

  // ==================================================================
  // Note graphics helpers
  // ==================================================================

  getNoteById(id: string): IPlayable | null {
    for (const staff of this.staffLines) {
      const found = staff.getNoteById(id);
      if (found) return found;
    }
    return null;
  }

  setNoteState({
    id,
    state,
    colorblindType,
  }: {
    id: string;
    state: ProgressState;
    colorblindType?: COLORBLIND_TYPE | null;
  }) {
    this.getNoteById(id)?.setTimingState(state, colorblindType);
  }

  pulseNote({ id, colorblindType }: { id: string; colorblindType?: COLORBLIND_TYPE | null }) {
    this.getNoteById(id)?.pulse(colorblindType);
  }

  getNotePosition(id: string) {
    for (const staff of this.staffLines) {
      const found = staff.getNotePosition(id);
      if (found) return found.x;
    }
    return null;
  }

  getPhraseEndPosition() {
    return Math.max(...this.staffLines.map((staffLine) => staffLine.getNextPosition()));
  }

  // ==================================================================
  // Scene stop / unload
  // ==================================================================

  stop() {
    this.tweens.killAll();
    this.scene.stop();
    this.unload();
  }

  unload() {
    this.lastTickTime = undefined;
    this.countInClicksHeard = null;
    this.playingState = PlayingState.Stopped;
    this.tweens.killAll();
    this.removeOwnListener('SetNoteState');
    this.removeOwnListener('PulseNote');
    this.unloadGraphics();
    super.unload();
  }
}
